import { useQuery } from "@hooks/useQuery";
import { Pagination, TableColumnsType, Typography } from "antd";
import { get as _get, debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { DataTable } from "~/Components";
import { TableDrawer } from "~/Components/TableDrawer/TableDrawer";
import { useNotification } from "~/Hooks/useNotification";
import { eramanBEApi, post } from "~/Services";
import { decodeHTML } from "~/Utils/CommonUtils";
import { drilldownData, ModuleConfig } from "~/Utils/drilldownData";
import { renderActionsColumn, renderDateColumn } from "~/Utils/TableRenderFunction";
import { useEdmMailerDocumentDetails } from "./EdmMailer.hook";

export const EdmMailer = () => {
  const query = useQuery();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [searchString, setSearchString] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [data, setData] = useState<any>([]);
  const [sortOrder, setSortOrder] = useState<string>("desc");
  const [orderByName, setOrderByName] = useState<string>("updatedAt");
  const [id, setId] = useState<string | undefined>(selectedRowData?.id);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const { openToast } = useNotification();

  const {
    data: drilldown,
    error,
    isLoading: drilldownLoading,
    isError,
    refetch: refetchDocument,
  } = useEdmMailerDocumentDetails(id || "");

  console.log("Users-hook", data, selectedRowData, drilldown);

  useEffect(() => {
    if (id) {
      refetchDocument();
    }
  }, [id]);

  useEffect(() => {
    setId(selectedRowData?.id);
  }, [selectedRowData]);

  const getRecords = useCallback(
    (sortOrderParam: string, orderByParam: string, searchStringParam: string, currentPageParam: number) => {
      setIsLoading(true);
      let url = `/admin/mails?pageNo=${currentPageParam}&pageSize=${10}&sortBy=${orderByParam}&sortOrder=${sortOrderParam ?? ""}&search=${encodeURIComponent(searchStringParam ?? "")}&type=Global`;

      eramanBEApi
        .get(url)
        .then((res: any) => {
          if (res && res.data) {
            setTotalRecords(res.data.totalRows);
            setData(res.data.data);
          }
        })
        .catch((err) => {
          setData([]);
          console.error("Error fetching data:", err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    []
  );

  useEffect(() => {
    getRecords(sortOrder, orderByName, searchString, currentPage);
  }, [getRecords, sortOrder, orderByName, searchString, currentPage]);

  const columns: TableColumnsType = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "title"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "title"
            ? "descend"
            : undefined,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (value: string) => {
        const decoded = decodeHTML(value || "");
        return decoded.replace(/<[^>]+>/g, ""); // remove <div> tags
      },
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "subject"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "subject"
            ? "descend"
            : undefined,
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: renderDateColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "createdAt"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "createdAt"
            ? "descend"
            : undefined,
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: renderDateColumn,
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "updatedAt"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "updatedAt"
            ? "descend"
            : undefined,
    },
    {
      title: "Updated By",
      dataIndex: "updateBy",
      key: "updateBy",
      sorter: true,
      sortOrder:
        sortOrder === "asc" && orderByName === "updateBy"
          ? "ascend"
          : sortOrder === "desc" && orderByName === "updateBy"
            ? "descend"
            : undefined,
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (_: any, record: any) => {
        console.log("record", record);
        return renderActionsColumn(
          record,
          setSelectedRowData,
          setDrawerVisible,
          (e: any) => {
            e.preventDefault();
            navigate(`/dedicated/eraman/configuration-edmMailer/add?editId=${record.id}`);
          },
          undefined,
          undefined,
          "edmMailer"
        );
      },
    },
  ];

  const handleTableSort = (column: any, sortOrder: string) => {
    let order = "";
    if (column.sorter) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
        order = "desc";
      } else if (sortOrder === "desc") {
        setSortOrder("asc");
        order = "asc";
      }
    }
    setOrderByName(column.dataIndex);
    // setCurrentPage(1);
    // getRecords(order, column.dataIndex, searchString, currentPage);
  };

  const handleSearchChange = debounce((value: string, isLoading: boolean) => {
    setSearchString(value);
    setIsLoading(isLoading);
    setCurrentPage(1);
  }, 1000);

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    if (apiUrl === "") return;

    const payload = {
      action: action,
      method: method,
      module: "Configuration",
      subModule: "users",
      type: "DEDICATED",
      application: "ERAMAN",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "ERAMAN",
          apiUrl: apiUrl,
        },
      ],
      payload: data,
      status: status,
    };

    post("/management/publish", payload)
      .then((response) => {
        openToast({
          content: "Data Sent for Review",
          type: "success",
        });
      })
      .catch((err: any) => {
        openToast({
          content: "Failed to send data",
          type: "error",
        });
      });
  };

  return (
    <>
      <DataTable
        tabOffset={60}
        columns={columns.map((col: any) => ({
          ...col,
          onHeaderCell: (column: any) => (col.sorter ? { onClick: () => handleTableSort(col, sortOrder) } : {}),
        }))}
        className="table-clickable"
        tableData={
          data
            ? data?.map((d: any) => {
                return {
                  ...d,
                };
              })
            : []
        }
        isLoading={isLoading}
        exportFileName="CONTRACT"
        isPagination={true}
        recordsPerPage={10}
        multiSelect={false}
        hideExport={true}
        isServerSearchApi={true}
        searchPlaceholderText={"Search Title / Description / Subject"}
        onSearchChange={handleSearchChange}
        onClick={(record) => {
          setSelectedRowData(record);
        }}
      />
      <div className="pagination">
        <Pagination
          defaultCurrent={1}
          current={currentPage}
          total={totalRecords}
          onChange={(page) => setCurrentPage(page)}
          showSizeChanger
          pageSizeOptions={["10", "20", "50", "100"]}
        />
      </div>
      <TableDrawer
        title={
          <Typography.Title
            level={4}
            className={"!mb-0"}
          >
            Detailed View
          </Typography.Title>
        }
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        fields={drilldownData[_get(drilldown, "subModule", "edmMailer") as ModuleConfig]}
        data={drilldown}
      />
    </>
  );
};
